<template>
  <article :id="id" class="game-card" :aria-labelledby="`${id}-title`">
    <img class="game-card__art" :src="image" :alt="imageAlt" loading="lazy" width="820" height="461" />
    <div class="game-card__intro">
      <p class="game-card__producer">{{ producer }}</p>
      <h2 :id="`${id}-title`" class="game-card__title">
        <span v-for="line in titleLines" :key="line">{{ line }}</span>
      </h2>
      <p class="game-card__description">{{ description }}</p>
      <a class="game-card__play" :href="href" target="_blank" rel="noopener noreferrer" :aria-label="`Play ${title} (opens in a new tab)`">
        <span class="game-card__play-lights" aria-hidden="true">
          <span v-for="light in 12" :key="light" class="game-card__play-light" :class="`game-card__play-light--${light}`"></span>
        </span>
        <span class="game-card__play-label">Play</span>
      </a>
    </div>
  </article>
</template>

<script>
export default {
  name: 'GameCard',
  props: {
    id: { type: String, required: true },
    title: { type: String, required: true },
    titleLines: { type: Array, required: true },
    producer: { type: String, required: true },
    description: { type: String, required: true },
    image: { type: String, required: true },
    imageAlt: { type: String, required: true },
    href: { type: String, required: true },
  },
};
</script>

<style scoped lang="scss">
.game-card {
  display: grid;
  grid-template-columns: minmax(0, 820fr) minmax(0, 400fr);
  align-items: center;
  gap: 32px;
  min-height: 516px;
  padding: 24px;
  border-radius: 28px;
  background: linear-gradient(152.11deg, #4d05b8 10.714%, #070123 82.143%);
  color: #fff;
  text-align: left;
}
.game-card__art {
  display: block;
  width: 100%;
  height: auto;
  aspect-ratio: 16 / 9;
  object-fit: contain;
  border-radius: 20px;
}
.game-card__intro {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 22px;
  min-width: 0;
  font-family: 'Poppins', sans-serif;
  font-weight: 400;
}
.game-card__producer, .game-card__title, .game-card__description { margin: 0; }
.game-card__producer { font-size: 14px; line-height: 1.5; }
.game-card__title {
  font-family: 'Paytone One', sans-serif;
  font-size: 48px;
  font-weight: 400;
  line-height: 1.15;
  color: #fff;
}
.game-card__title span { display: block; }
.game-card__description { font-size: 22px; line-height: 1.5; }
#carnival-cowboys .game-card__description { font-size: 24px; }
.game-card__play {
  position: relative;
  isolation: isolate;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  padding: 14px 28px;
  border-radius: 28px;
  background: radial-gradient(circle, #ffd215, #fff172 80%);
  box-shadow: 0 0 14px rgba(255, 223, 87, .5);
  color: #fff;
  font-family: 'Paytone One', sans-serif;
  font-size: 18px;
  font-weight: 400;
  line-height: 1.15;
  text-decoration: none;
}
/* Win Box's continuously moving, blurred lights, clipped below the label. */
.game-card__play::before {
  content: '';
  position: absolute;
  z-index: 2;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  box-shadow: inset 0 3px 12px rgba(255, 223, 52, .9), inset 0 -3px 4px rgba(255, 250, 215, .8);
}
.game-card__play-lights {
  position: absolute;
  z-index: 0;
  inset: 0;
  overflow: hidden;
  border-radius: inherit;
  pointer-events: none;
  -webkit-mask-image: -webkit-radial-gradient(white, black);
}
.game-card__play-light {
  position: absolute;
  left: 0;
  top: 0;
  width: 30%;
  aspect-ratio: 1;
  border-radius: 50%;
  background: var(--light-color, rgba(255, 163, 26, .7));
  filter: blur(var(--light-blur, 9px));
  transform: translate(var(--light-x), var(--light-y)) translateZ(0);
  animation-duration: 4s;
  animation-timing-function: linear;
  animation-iteration-count: infinite;
}
.game-card__play-label { position: relative; z-index: 1; }
.game-card__play-light--1 { --light-x: -7.44px; --light-y: -14.89px; --light-blur: 6px; animation-name: play-light-1; --light-color: rgba(255, 232, 26, .7); }
.game-card__play-light--2 { --light-x: 68.47px; --light-y: 5.95px; --light-blur: 9px; animation-name: play-light-2; }
.game-card__play-light--3 { --light-x: -8.93px; --light-y: -8.93px; --light-blur: 10.42px; animation-name: play-light-3; --light-color: #1a23ff; }
.game-card__play-light--4 { --light-x: 59.54px; --light-y: -8.93px; --light-blur: 10.42px; animation-name: play-light-4; --light-color: #1a23ff; left: 30%; }
.game-card__play-light--5 { --light-x: 8.93px; --light-y: -2.98px; --light-blur: 11.91px; animation-name: play-light-5; --light-color: #e21bda; }
.game-card__play-light--6 { --light-x: 41.68px; --light-y: 11.91px; --light-blur: 11.91px; animation-name: play-light-6; --light-color: #e21bda; }
.game-card__play-light--7 { --light-x: 5.95px; --light-y: 20.84px; --light-blur: 9px; animation-name: play-light-7; }
.game-card__play-light--8 { --light-x: 20.84px; --light-y: -2.98px; --light-blur: 9px; animation-name: play-light-8; }
.game-card__play-light--9 { --light-x: 14.89px; --light-y: -8.93px; --light-blur: 10.42px; animation-name: play-light-9; --light-color: rgba(255, 232, 26, .7); }
.game-card__play-light--10 { --light-x: 47.63px; --light-y: 11.91px; --light-blur: 9px; animation-name: play-light-10; --light-color: rgba(255, 232, 26, .7); }
.game-card__play-light--11 { --light-x: 2.98px; --light-y: 2.98px; --light-blur: 9px; animation-name: play-light-11; }
.game-card__play-light--12 { --light-x: 38.7px; --light-y: 2.98px; --light-blur: 10.42px; animation-name: play-light-12; }
@keyframes play-light-1 {
  33% { transform: translate(0px, 16px) translateZ(0); }
  66% { transform: translate(12px, 64px) translateZ(0); }
}
@keyframes play-light-2 {
  33% { transform: translate(80px, -10px) translateZ(0); }
  66% { transform: translate(72px, -48px) translateZ(0); }
}
@keyframes play-light-3 {
  33% { transform: translate(20px, 12px) translateZ(0); }
  66% { transform: translate(12px, 4px) translateZ(0); }
}
@keyframes play-light-4 {
  33% { transform: translate(76px, -12px) translateZ(0); }
  66% { transform: translate(112px, -8px) translateZ(0); }
}
@keyframes play-light-5 {
  33% { transform: translate(84px, 28px) translateZ(0); }
  66% { transform: translate(40px, -32px) translateZ(0); }
}
@keyframes play-light-6 {
  33% { transform: translate(28px, -16px) translateZ(0); }
  66% { transform: translate(76px, -56px) translateZ(0); }
}
@keyframes play-light-7 {
  33% { transform: translate(8px, 28px) translateZ(0); }
  66% { transform: translate(20px, -60px) translateZ(0); }
}
@keyframes play-light-8 {
  33% { transform: translate(32px, -4px) translateZ(0); }
  66% { transform: translate(56px, -20px) translateZ(0); }
}
@keyframes play-light-9 {
  33% { transform: translate(20px, -12px) translateZ(0); }
  66% { transform: translate(80px, -8px) translateZ(0); }
}
@keyframes play-light-10 {
  33% { transform: translate(68px, 20px) translateZ(0); }
  66% { transform: translate(100px, 28px) translateZ(0); }
}
@keyframes play-light-11 {
  33% { transform: translate(4px, 4px) translateZ(0); }
  66% { transform: translate(68px, 20px) translateZ(0); }
}
@keyframes play-light-12 {
  33% { transform: translate(56px, 0px) translateZ(0); }
  66% { transform: translate(60px, -32px) translateZ(0); }
}
@media (prefers-reduced-motion: reduce) {
  .game-card__play-light { animation: none; }
}
.game-card__play:hover { filter: brightness(1.08); }
.game-card__play:focus-visible { outline: 3px solid #fff; outline-offset: 4px; }
@media (max-width: 1100px) {
  .game-card { grid-template-columns: minmax(0, 1.5fr) minmax(0, 1fr); min-height: 0; }
  .game-card__title { font-size: 36px; }
  .game-card__description, #carnival-cowboys .game-card__description { font-size: 20px; }
}
@media (max-width: 760px) {
  .game-card { grid-template-columns: minmax(0, 1fr); gap: 20px; padding: 20px; border-radius: 20px; background: linear-gradient(122.54deg, #4d05b8 10.714%, #070123 82.143%); }
  .game-card__intro { gap: 20px; }
  .game-card__producer { display: none; }
  .game-card__title { font-size: 30px; }
  .game-card__title span { display: inline; }
  .game-card__title span + span::before { content: ' '; }
  .game-card__description, #carnival-cowboys .game-card__description { font-size: 18px; }
}
</style>
